"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Sidebar from '../../components/layout/Sidebar';
import SearchPrompt from '../../components/ui/SearchPrompt';
import MobileNavigation from '../../components/layout/MobileNavigation';
import MobileHeader from '../../components/layout/MobileHeader';
import OpenMenuLogo from '../../../public/logo';

export default function CategoryPage({ params }) {
  // Get the slug directly from params without using React.use()
  const { slug } = params;
  
  // State to track the current view type
  const [viewType, setViewType] = useState('tile'); // 'tile', 'column', or 'map'

  // State to track visible items for load more functionality
  const [visibleItems, setVisibleItems] = useState(6);
  
  // State to track mobile sidebar open state
  const [mobileOpen, setMobileOpen] = useState(false);

  // For filter/sort functionality
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    priceRange: [],
    cuisines: [],
    dietary: [],
    rating: null,
    distance: null,
  });
  const [sortBy, setSortBy] = useState('popular'); // 'popular', 'rating', 'distance', or 'priceAsc', 'priceDesc'
  
  // Get the category info based on slug
  const getCategoryInfo = (slug) => {
    const categoryMap = {
      'fast-food': { 
        name: 'Fast Food', 
        image: '🍔', 
        key: 'fastFood', 
        description: 'Discover the best fast food restaurants in your area.' 
      },
      'hi-tea': { 
        name: 'Hi-Tea Deals', 
        image: '☕', 
        key: 'hiTea', 
        description: 'Explore exclusive hi-tea deals from top cafes and restaurants.' 
      },
      'desi-food': { 
        name: 'Desi Food', 
        image: '🍲', 
        key: 'desiFood', 
        description: 'Authentic desi cuisine from the best local restaurants.' 
      },
      'healthy': { 
        name: 'Healthy Options', 
        image: '🥗', 
        key: 'healthy', 
        description: 'Nutritious meals for health-conscious food lovers.' 
      }
    };
    
    return categoryMap[slug] || { 
      name: 'Category',
      image: '🍽️',
      key: 'unknown',
      description: 'Explore restaurants in this category'
    };
  };
  
  const category = getCategoryInfo(slug);
  
  // Handle loading more items
  const handleLoadMore = () => {
    setVisibleItems(prev => prev + 6);
  };
  
  // Sample restaurant data for the category
  const restaurants = {
    "fastFood": [
      {
        id: 1,
        name: "Burger King",
        rating: 4.5,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-amber-400"
      },
      {
        id: 2,
        name: "Pizza Hut",
        rating: 4.2,
        deliveryTime: "30-45 min",
        priceRange: "RsRsRs",
        categories: ["Pizza", "Italian"],
        image: "🍕",
        bgColor: "bg-red-600"
      },
      {
        id: 3,
        name: "KFC",
        rating: 4,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Fried Chicken"],
        image: "🍗",
        bgColor: "bg-amber-400"
      },
      {
        id: 8,
        name: "McDonald's", 
        rating: 4.3,
        deliveryTime: "15-25 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-red-500"
      },
      {
        id: 9,
        name: "Subway",
        rating: 4.1,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Sandwiches"],
        image: "🥪",
        bgColor: "bg-green-600"
      },
      {
        id: 10,
        name: "Hardee's",
        rating: 4.4,
        deliveryTime: "25-40 min",
        priceRange: "RsRsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-amber-700"
      },
      {
        id: 18,
        name: "Nawabs",
        rating: 4.2,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Burgers"],
        image: "🍔",
        bgColor: "bg-red-400"
      },
      {
        id: 19,
        name: "Domino's Pizza",
        rating: 4.0,
        deliveryTime: "30-40 min",
        priceRange: "RsRs",
        categories: ["Fast Food", "Pizza"],
        image: "🍕",
        bgColor: "bg-blue-600"
      },
      {
        id: 20,
        name: "Chillis",
        rating: 4.6,
        deliveryTime: "30-45 min",
        priceRange: "RsRsRs",
        categories: ["Fast Food", "American"],
        image: "🌮",
        bgColor: "bg-red-600"
      }
    ],
    "hiTea": [
      {
        id: 4,
        name: "English Tea House",
        rating: 4.7,
        deliveryTime: "15-25 min",
        priceRange: "RsRsRs",
        categories: ["Hi-Tea", "Desserts"],
        image: "🍵",
        bgColor: "bg-green-600"
      },
      {
        id: 5,
        name: "Chai Spot",
        rating: 4.3,
        deliveryTime: "20-30 min",
        priceRange: "Rs",
        categories: ["Hi-Tea", "Local"],
        image: "☕",
        bgColor: "bg-amber-700"
      },
      {
        id: 11,
        name: "Cafe Aylanto",
        rating: 4.6,
        deliveryTime: "25-35 min",
        priceRange: "RsRsRs",
        categories: ["Hi-Tea", "Cafe"],
        image: "🧁",
        bgColor: "bg-pink-500"
      },
      {
        id: 12,
        name: "Gloria Jean's",
        rating: 4.2,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Hi-Tea", "Coffee"],
        image: "☕",
        bgColor: "bg-amber-800"
      },
      {
        id: 21,
        name: "Butlers Chocolate Cafe",
        rating: 4.5,
        deliveryTime: "15-30 min",
        priceRange: "RsRsRs",
        categories: ["Hi-Tea", "Desserts"],
        image: "🍮",
        bgColor: "bg-amber-600"
      },
      {
        id: 22,
        name: "Mocca Coffee",
        rating: 4.1,
        deliveryTime: "20-30 min",
        priceRange: "RsRs",
        categories: ["Hi-Tea", "Coffee"],
        image: "☕",
        bgColor: "bg-amber-500"
      },
      {
        id: 23,
        name: "Jade Cafe",
        rating: 4.4,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Hi-Tea", "Pastries"],
        image: "🥐",
        bgColor: "bg-green-500"
      }
    ],
    "desiFood": [
      {
        id: 6,
        name: "Karachi Biryani",
        rating: 4.6,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Desi", "Biryani"],
        image: "🍚",
        bgColor: "bg-orange-500"
      },
      {
        id: 7,
        name: "Lahori Dhaba",
        rating: 4.5,
        deliveryTime: "30-40 min",
        priceRange: "RsRs",
        categories: ["Desi", "Punjabi"],
        image: "🍛",
        bgColor: "bg-red-500"
      },
      {
        id: 13,
        name: "Bundu Khan",
        rating: 4.4,
        deliveryTime: "30-45 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "BBQ"],
        image: "🍖",
        bgColor: "bg-amber-600"
      },
      {
        id: 14,
        name: "Student Biryani",
        rating: 4.2,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Desi", "Biryani"],
        image: "🍚",
        bgColor: "bg-yellow-600"
      },
      {
        id: 15,
        name: "Salt'n Pepper",
        rating: 4.7,
        deliveryTime: "35-50 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "Fine Dining"],
        image: "🍲",
        bgColor: "bg-red-700"
      },
      {
        id: 24,
        name: "Kolachi",
        rating: 4.8,
        deliveryTime: "40-55 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "BBQ"],
        image: "🥩",
        bgColor: "bg-red-600"
      },
      {
        id: 25,
        name: "BBQ Tonight",
        rating: 4.6,
        deliveryTime: "35-45 min",
        priceRange: "RsRsRs",
        categories: ["Desi", "BBQ"],
        image: "🍖",
        bgColor: "bg-red-500"
      },
      {
        id: 26,
        name: "Darbar",
        rating: 4.3,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Desi", "Karahi"],
        image: "🍲",
        bgColor: "bg-orange-600"
      }
    ],
    "healthy": [
      {
        id: 16,
        name: "Freshii",
        rating: 4.5,
        deliveryTime: "20-30 min",
        priceRange: "RsRsRs",
        categories: ["Healthy", "Salads"],
        image: "🥗",
        bgColor: "bg-green-500"
      },
      {
        id: 17,
        name: "Protein Shack",
        rating: 4.3,
        deliveryTime: "25-35 min",
        priceRange: "RsRs",
        categories: ["Healthy", "Protein"],
        image: "🥑",
        bgColor: "bg-green-600"
      },
      {
        id: 27,
        name: "Juicy Lucy",
        rating: 4.4,
        deliveryTime: "15-25 min",
        priceRange: "RsRs",
        categories: ["Healthy", "Juices"],
        image: "🥤",
        bgColor: "bg-yellow-500"
      },
      {
        id: 28,
        name: "Green Salad Bar",
        rating: 4.6,
        deliveryTime: "20-30 min",
        priceRange: "RsRsRs",
        categories: ["Healthy", "Vegetarian"],
        image: "🥬",
        bgColor: "bg-green-500"
      }
    ]
  };
  
  // Get restaurants for current category
  const categoryRestaurants = restaurants[category.key] || [];
  
  // Generate a URL-friendly slug from restaurant name
  const getRestaurantSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  };
  
  const renderRestaurantCard = (restaurant) => (
    <Link href={`/restaurant/${getRestaurantSlug(restaurant.name)}`} key={restaurant.id} className="block">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100">
        <div className="flex items-center p-4">
          <div className={`${restaurant.bgColor} text-white p-3 rounded-lg mr-4 text-2xl`}>
            {restaurant.image}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-800">{restaurant.name}</h3>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <span className="flex items-center">
                ⭐ {restaurant.rating}
              </span>
              <span className="mx-2">•</span>
              <span>{restaurant.deliveryTime}</span>
              <span className="mx-2">•</span>
              <span>{restaurant.priceRange}</span>
            </div>
            <div className="flex mt-2">
              {restaurant.categories.map((cat, idx) => (
                <span key={idx} className="text-xs mr-2 px-2 py-1 bg-gray-100 rounded-full text-gray-600">{cat}</span>
              ))}
            </div>
          </div>
          <button className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-opacity-90 transition-colors ml-2">
            Order
          </button>
        </div>
      </div>
    </Link>
  );
  
  // Render Grid View (tiles in a grid)
  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-4">
      {categoryRestaurants.length > 0 ? (
        categoryRestaurants.slice(0, visibleItems).map(restaurant => renderRestaurantCard(restaurant))
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
          No restaurants found in this category.
        </div>
      )}
    </div>
  );
  
  // Render Column View (smaller cards in columns)
  const renderColumnView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {categoryRestaurants.length > 0 ? (
        categoryRestaurants.slice(0, visibleItems).map(restaurant => (
          <Link href={`/restaurant/${getRestaurantSlug(restaurant.name)}`} key={restaurant.id} className="block">
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100">
              <div className="p-3">
                <div className="flex items-center mb-2">
                  <div className={`${restaurant.bgColor} text-white p-2 rounded-lg mr-3 text-xl`}>
                    {restaurant.image}
                  </div>
                  <h3 className="font-semibold text-base text-gray-800">{restaurant.name}</h3>
                </div>
                <div className="flex items-center text-xs text-gray-500">
                  <span className="flex items-center">
                    ⭐ {restaurant.rating}
                  </span>
                  <span className="mx-1">•</span>
                  <span>{restaurant.deliveryTime}</span>
                  <span className="mx-1">•</span>
                  <span>{restaurant.priceRange}</span>
                </div>
                <div className="flex mt-2 flex-wrap">
                  {restaurant.categories.map((cat, idx) => (
                    <span key={idx} className="text-xs mr-1 mb-1 px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{cat}</span>
                  ))}
                </div>
                <button className="bg-primary text-white rounded-lg w-full py-1.5 text-sm font-medium hover:bg-opacity-90 transition-colors mt-2">
                  Order
                </button>
              </div>
            </div>
          </Link>
        ))
      ) : (
        <div className="col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
          No restaurants found in this category.
        </div>
      )}
    </div>
  );
  
  // Render Map View (placeholder for future)
  const renderMapView = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center h-96 flex items-center justify-center flex-col">
      <div className="text-6xl mb-4">🗺️</div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">Map View Coming Soon</h3>
      <p className="text-gray-500 max-w-md">
        We're working on a map view that will show restaurants near your location.
        Stay tuned for this exciting feature!
      </p>
    </div>
  );
  
  return (
    <main className="flex min-h-screen bg-gray-50">
      {/* Sidebar - Hidden on mobile, toggleable via drawer */}
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      {/* Mobile Header - Visible only on mobile */}
      <MobileHeader setMobileOpen={setMobileOpen} />

      <div className="flex-1 py-16 md:py-8 md:pt-8 px-4">
        {/* Header Section */}
        <div className="mb-8 max-w-4xl mx-auto">
          <div className="flex items-center mb-3">
            <Link href="/" className="flex items-center text-primary hover:text-primary-dark mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back</span>
            </Link>
            <div className="mx-2 text-gray-300">|</div>
            <div className="flex items-center">
              <span className="text-3xl mr-2">{category.image}</span>
              <h2 className="text-2xl font-bold text-gray-800">{category.name}</h2>
            </div>
          </div>
          <p className="text-gray-500 max-w-xl text-sm">
            {category.description}
          </p>
        </div>

        {/* View Toggle Options */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-gray-800 font-medium">
              {categoryRestaurants.length} {categoryRestaurants.length === 1 ? 'Restaurant' : 'Restaurants'} Found
            </div>
            <div className="flex items-center">
              <div className="text-sm text-gray-500 mr-2">View:</div>
              <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button 
                  onClick={() => setViewType('tile')} 
                  className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'tile' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  title="Tile View"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  Tile
                </button>
                <button 
                  onClick={() => setViewType('column')} 
                  className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'column' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  title="Column View"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  Column
                </button>
                <button 
                  onClick={() => setViewType('map')} 
                  className={`px-3 py-1.5 text-sm flex items-center ${viewType === 'map' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  title="Map View (Coming Soon)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Map
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Restaurant List - Based on selected view type */}
        <div className="max-w-4xl mx-auto mb-8">
          {viewType === 'tile' && renderGridView()}
          {viewType === 'column' && renderColumnView()}
          {viewType === 'map' && renderMapView()}
        </div>
        
        {/* Load More Button */}
        {categoryRestaurants.length > visibleItems && (
          <div className="max-w-4xl mx-auto text-center mb-12">
            <button 
              onClick={handleLoadMore}
              className="px-6 py-2.5 bg-white border border-gray-200 rounded-lg text-primary font-medium hover:bg-gray-50 transition-colors shadow-sm"
            >
              Load More
            </button>
          </div>
        )}

        {/* SearchPrompt component at the bottom */}
        <SearchPrompt />
      </div>

      {/* Mobile Bottom Navigation - Visible only on mobile */}
      <MobileNavigation />
    </main>
  );
} 